import type {
  CreateOpsUserResponse,
  DeleteSalesProfileResponse,
  DisableOpsUserResponse,
  EnableOpsUserResponse,
  GetSalesProfileResponse,
  InviteOpsUserPasswordResponse,
  ListOpsUsersResponse,
  OpsUserRecord,
  ProvisionSalespersonRequest,
  ProvisionSalespersonResponse,
  ResetOpsUserPasswordResponse,
  RevokeOpsUserSessionsResponse,
  SetOpsUserPasswordResponse,
  SalesProfileRecord,
  UpdateOpsUserResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';
import { INVITE_CODES_QUERY_KEY, INVITE_CODE_SALESPEOPLE_QUERY_KEY } from './inviteCodes.ts';

const BASE = '/v1/ops/ops-users';
const OPS_USERS_KEY = ['ops-users'] as const;

export async function listOpsUsers(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListOpsUsersResponse> {
  return opsApiRequest<ListOpsUsersResponse>(BASE, { signal }, fetchImpl);
}

export async function disableOpsUser(
  id: string,
  fetchImpl: typeof fetch,
): Promise<OpsUserRecord> {
  const response = await opsApiRequest<DisableOpsUserResponse>(
    `${BASE}/${encodeURIComponent(id)}/disable`,
    { method: 'POST' },
    fetchImpl,
  );
  return response.user;
}

export interface SaveOpsUserInput {
  email: string;
  display_name?: string | null;
  roles: string[];
  feishu_tenant_key?: string | null;
  feishu_open_id?: string | null;
  feishu_union_id?: string | null;
  feishu_user_id?: string | null;
  feishu_name?: string | null;
  feishu_avatar_url?: string | null;
  feishu_enterprise_email?: string | null;
}

export interface UpdateOpsUserInput {
  id: string;
  email?: string;
  display_name?: string | null;
  roles?: string[];
  feishu_tenant_key?: string | null;
  feishu_open_id?: string | null;
  feishu_union_id?: string | null;
  feishu_user_id?: string | null;
  feishu_name?: string | null;
  feishu_avatar_url?: string | null;
  feishu_enterprise_email?: string | null;
}

export async function createOpsUser(
  input: SaveOpsUserInput,
  fetchImpl: typeof fetch,
): Promise<OpsUserRecord> {
  const response = await opsApiRequest<CreateOpsUserResponse>(
    BASE,
    { method: 'POST', body: input },
    fetchImpl,
  );
  return response.user;
}

export async function updateOpsUser(
  input: UpdateOpsUserInput,
  fetchImpl: typeof fetch,
): Promise<OpsUserRecord> {
  const { id, ...body } = input;
  const response = await opsApiRequest<UpdateOpsUserResponse>(
    `${BASE}/${encodeURIComponent(id)}`,
    { method: 'PATCH', body },
    fetchImpl,
  );
  return response.user;
}

export async function enableOpsUser(
  id: string,
  fetchImpl: typeof fetch,
): Promise<OpsUserRecord> {
  const response = await opsApiRequest<EnableOpsUserResponse>(
    `${BASE}/${encodeURIComponent(id)}/enable`,
    { method: 'POST' },
    fetchImpl,
  );
  return response.user;
}

export async function revokeOpsUserSessions(
  id: string,
  fetchImpl: typeof fetch,
): Promise<RevokeOpsUserSessionsResponse> {
  return opsApiRequest<RevokeOpsUserSessionsResponse>(
    `${BASE}/${encodeURIComponent(id)}/revoke-sessions`,
    { method: 'POST' },
    fetchImpl,
  );
}

export async function resetOpsUserPassword(
  id: string,
  fetchImpl: typeof fetch,
): Promise<ResetOpsUserPasswordResponse> {
  return opsApiRequest<ResetOpsUserPasswordResponse>(
    `${BASE}/${encodeURIComponent(id)}/reset-password`,
    { method: 'POST' },
    fetchImpl,
  );
}

export async function setOpsUserPassword(
  id: string,
  password: string,
  fetchImpl: typeof fetch,
): Promise<SetOpsUserPasswordResponse> {
  return opsApiRequest<SetOpsUserPasswordResponse>(
    `${BASE}/${encodeURIComponent(id)}/set-password`,
    { method: 'POST', body: { password } },
    fetchImpl,
  );
}

export async function inviteOpsUserPassword(
  id: string,
  fetchImpl: typeof fetch,
): Promise<InviteOpsUserPasswordResponse> {
  return opsApiRequest<InviteOpsUserPasswordResponse>(
    `${BASE}/${encodeURIComponent(id)}/invite-password`,
    { method: 'POST' },
    fetchImpl,
  );
}

export function useOpsUsers(
  options: { readonly enabled?: boolean } = {},
): UseQueryResult<ListOpsUsersResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: OPS_USERS_KEY,
    queryFn: ({ signal }) => listOpsUsers(fetchImpl, signal),
    enabled: options.enabled ?? true,
  });
}

export function useDisableOpsUser(): UseMutationResult<OpsUserRecord, Error, string> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => disableOpsUser(id, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: OPS_USERS_KEY });
    },
  });
}

export function useCreateOpsUser(): UseMutationResult<OpsUserRecord, Error, SaveOpsUserInput> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => createOpsUser(input, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: OPS_USERS_KEY });
    },
  });
}

export function useUpdateOpsUser(): UseMutationResult<OpsUserRecord, Error, UpdateOpsUserInput> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => updateOpsUser(input, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: OPS_USERS_KEY });
    },
  });
}

export function useEnableOpsUser(): UseMutationResult<OpsUserRecord, Error, string> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => enableOpsUser(id, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: OPS_USERS_KEY });
    },
  });
}

export function useRevokeOpsUserSessions(): UseMutationResult<
  RevokeOpsUserSessionsResponse,
  Error,
  string
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => revokeOpsUserSessions(id, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: OPS_USERS_KEY });
    },
  });
}

export function useResetOpsUserPassword(): UseMutationResult<
  ResetOpsUserPasswordResponse,
  Error,
  string
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => resetOpsUserPassword(id, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: OPS_USERS_KEY });
    },
  });
}

export function useSetOpsUserPassword(): UseMutationResult<
  SetOpsUserPasswordResponse,
  Error,
  { readonly id: string; readonly password: string }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => setOpsUserPassword(input.id, input.password, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: OPS_USERS_KEY });
    },
  });
}

export function useInviteOpsUserPassword(): UseMutationResult<
  InviteOpsUserPasswordResponse,
  Error,
  string
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => inviteOpsUserPassword(id, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: OPS_USERS_KEY });
    },
  });
}

// ─── one-click salesperson provisioning ──────────────────────────────────

/**
 * The response is the ONLY place the generated password ever appears. It is
 * deliberately not written into the React Query cache — the mutation result
 * lives in the sheet's own state and dies with the component, so a stale
 * cache read can never resurrect it.
 */
export async function provisionSalesperson(
  input: ProvisionSalespersonRequest,
  fetchImpl: typeof fetch,
): Promise<ProvisionSalespersonResponse> {
  return opsApiRequest<ProvisionSalespersonResponse>(
    `${BASE}/provision-salesperson`,
    { method: 'POST', body: input },
    fetchImpl,
  );
}

export function useProvisionSalesperson(): UseMutationResult<
  ProvisionSalespersonResponse,
  Error,
  ProvisionSalespersonRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => provisionSalesperson(input, fetchImpl),
    onSuccess: () => {
      // One call writes three things: an ops user, a salesperson profile, and
      // an invite code. Invalidate all three — since #2022 moved the entry
      // point onto the invite-codes page, the operator lands back on a list
      // that must already show the code they just minted, and on a mint dialog
      // whose salesperson picker must already offer the person they just
      // created. `staleTime` is 30s globally, so without this the page lies.
      void qc.invalidateQueries({ queryKey: OPS_USERS_KEY });
      void qc.invalidateQueries({ queryKey: INVITE_CODES_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: INVITE_CODE_SALESPEOPLE_QUERY_KEY });
    },
  });
}

// ─── sales profile (epic #1030 E1-S1, #1037) ─────────────────────────────

function salesProfileKey(id: string): readonly [string, string, string] {
  return ['ops-users', id, 'sales-profile'] as const;
}

export async function getSalesProfile(
  id: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<SalesProfileRecord | null> {
  const response = await opsApiRequest<GetSalesProfileResponse>(
    `${BASE}/${encodeURIComponent(id)}/sales-profile`,
    { signal },
    fetchImpl,
  );
  return response.profile;
}

export async function setSalesProfile(
  id: string,
  brand: string,
  fetchImpl: typeof fetch,
): Promise<SalesProfileRecord> {
  const response = await opsApiRequest<{ profile: SalesProfileRecord }>(
    `${BASE}/${encodeURIComponent(id)}/sales-profile`,
    { method: 'PUT', body: { brand } },
    fetchImpl,
  );
  return response.profile;
}

export async function deleteSalesProfile(
  id: string,
  fetchImpl: typeof fetch,
): Promise<DeleteSalesProfileResponse> {
  return opsApiRequest<DeleteSalesProfileResponse>(
    `${BASE}/${encodeURIComponent(id)}/sales-profile`,
    { method: 'DELETE' },
    fetchImpl,
  );
}

export function useSalesProfile(id: string): UseQueryResult<SalesProfileRecord | null> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: salesProfileKey(id),
    queryFn: ({ signal }) => getSalesProfile(id, fetchImpl, signal),
  });
}

export function useSetSalesProfile(): UseMutationResult<
  SalesProfileRecord,
  Error,
  { readonly id: string; readonly brand: string }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => setSalesProfile(input.id, input.brand, fetchImpl),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: salesProfileKey(input.id) });
    },
  });
}

export function useDeleteSalesProfile(): UseMutationResult<
  DeleteSalesProfileResponse,
  Error,
  string
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteSalesProfile(id, fetchImpl),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: salesProfileKey(id) });
    },
  });
}
